/**
 * RWBR — WHAT THESE TWO HAVE ALREADY DONE TO EACH OTHER. D-EZ-56.
 *
 * Howard, 18 Aug 2026: *"Would you be able to track the interactions on the
 * track and off for feuds here before the race, maybe behind a drop down
 * chevron?"*
 *
 * ===========================================================================
 * NOTHING IS STORED. THIS IS A READING OF THE LEDGER.
 * ===========================================================================
 *
 * TELEMETRY_SPEC's rule is derive, never duplicate, and a feud is the strongest
 * case for it in the whole game: the moment a grudge keeps its own list of
 * grievances, that list can disagree with the ledger, and then the screen is
 * telling a story the game did not play. So there is no feud log anywhere in
 * `SeasonState`. There is the season's one append-only stream, and this folds
 * it — every event both men appear in, in the order it happened.
 *
 * That is also why this needs no migration and works on a season saved before
 * it existed. The history was always in there; nobody had read it out.
 *
 * ===========================================================================
 * BOTH MEN, OR IT IS NOT AN INTERACTION
 * ===========================================================================
 *
 * An event counts as an interaction in exactly two ways:
 *
 *   pointed  — both men are named in `actorIds` AND the moment is about three
 *              men or fewer. A moment about two men names two men: a duel, a
 *              challenge, an altercation off the racetrack, the FEUD/FUME roll.
 *   shared   — ON THE RACETRACK ONLY, both men were MOVED by it, whoever it
 *              names. A wreck that collects the pair of them names the man who
 *              caused it and moves both, and a player looking at a grudge wants
 *              that row.
 *
 * Anything else is not an interaction, however many times it says their names.
 *
 * ⚠ BOTH HALVES OF THAT SECOND RULE ARE DELETIONS, AND BOTH CAME OUT OF THE
 * SAME SCREENSHOT. Testing only "are they both in `actorIds`" filled a two-man
 * grudge with the week's bookkeeping: *"The metallic dice come up 3 and 5 — 8
 * drivers on this week's TV LIST"*, *"22 drivers come off the bubble to round
 * the field out to 43"*, *"Cooter Holcomb leads the field to the green with 43
 * cars starting."* All three name both men. None of them is a thing that
 * happened BETWEEN them.
 *
 * Requiring the event to have MOVED them removes the roll calls. Requiring a
 * shared beat to be on the RACETRACK removes the rest, and it is not a
 * heuristic: race week does not put two men in the same wreck, it deals cards
 * and assigns a starting grid, so every driver on the entry list is "moved" by
 * STEP SIX. Off the racetrack the only thing that can happen between two
 * drivers is a chart row naming them both — which is pointed, and kept.
 *
 * The screen prints both kinds and says which is which. Collapsing them would
 * let a coincidence read as a grudge, and this project does not ship that.
 */

import type { DiceRecord, RuleEvent } from './ruleEvent.ts';
import type { SeasonEvent } from './standings.ts';
import type { FeudEntry } from './types.ts';

// ---------------------------------------------------------------------------
// The shape of one run-in
// ---------------------------------------------------------------------------

/** On the racetrack, or somewhere a television camera found them. */
export type FeudBeatWhere = 'track' | 'off';

/** Whether they went at each other, or the same thing happened to both. */
export type FeudBeatWeight = 'pointed' | 'shared';

export interface FeudBeat {
  /** Stable across renders: stream position, tag and pair. */
  id: string;
  /** Position in the season stream. `-1` for a beat from the week in hand. */
  sequence: number;
  raceNumber: number | null;
  raceName: string | null;
  /** The race turn, where it had one. Race week has none. */
  turn: number | null;
  where: FeudBeatWhere;
  weight: FeudBeatWeight;
  /** Two or three words: "Duel", "Altercation", "They went at it". */
  label: string;
  /** ⚠ THE LEDGER'S OWN SENTENCE, VERBATIM. D-003 — nothing here rewrites it. */
  text: string;
  citation: string;
  /**
   * The moment's own rolls, kept whole rather than flattened to faces — a roll
   * carries its `kind` and its named faces, and that is what colours a die red
   * or black on screen. Flattening loses it.
   */
  dice: readonly DiceRecord[];
  momentTag: string;
}

export interface FeudHistory {
  key: string;
  a: string;
  b: string;
  /** Oldest first — a grudge reads forward. Capped; see `total`. */
  beats: FeudBeat[];
  /** How many run-ins there are in the ledger, before the cap. */
  total: number;
  onTrack: number;
  offTrack: number;
  /** How many of them were pointed rather than shared misfortune. */
  pointed: number;
  /** Race numbers they tangled in, ascending. */
  races: number[];
  firstRace: number | null;
  /**
   * The earliest POINTED beat — the one a reader means by "what started it".
   * Null when everything between them so far is coincidence.
   */
  origin: FeudBeat | null;
}

/**
 * Forty rows is not a story, it is a log file. The most recent twenty are kept
 * and `total` says what was left out, so the screen can be honest about the cut
 * rather than quietly truncating.
 */
export const DEFAULT_MAX_BEATS = 20;

/** Unordered — a feud is not directional, and neither is its file. */
export function feudKeyOf(a: string, b: string): string {
  return a < b ? `${a} ${b}` : `${b} ${a}`;
}

// ---------------------------------------------------------------------------
// Reading a moment tag
// ---------------------------------------------------------------------------

/**
 * ⚠ TAG PREFIXES, NOT AN EXHAUSTIVE TABLE, and that is deliberate. A new chart
 * module lands with a new tag and this still files it somewhere sensible rather
 * than throwing or printing a raw `season.foo.bar` at a player. The fallback
 * humanises the tail of the tag, which is how these tags are written anyway.
 *
 * Order matters: a specific row sits above the prefix it belongs to.
 */
const LABELS: readonly (readonly [string, string])[] = [
  ['raceweek.feud', 'Altercation'],
  ['raceweek.fume-list', 'On the fume list'],
  ['raceweek.unavailable', 'Out of the race'],
  ['raceweek.', 'Off the racetrack'],
  ['season.feud-loser.', 'After the duel'],
  ['season.feud-carry.', 'Still on the list'],
  ['season.', 'Between the races'],
  ['challenge.', 'Challenge'],
  ['duel.', 'Duel'],
  ['feud.', 'They went at it'],
  ['pit.', 'On pit road'],
  ['trouble.', 'Trouble'],
  ['wreck.', 'The big one'],
  ['cam.', 'On camera'],
];

export function labelFor(momentTag: string): string {
  for (const [prefix, label] of LABELS) {
    if (momentTag === prefix || momentTag.startsWith(prefix)) return label;
  }
  const tail = momentTag.split('.').pop() ?? momentTag;
  const words = tail.replace(/[-_]/g, ' ').trim();
  return words.length === 0 ? 'A moment' : words[0].toUpperCase() + words.slice(1);
}

/**
 * Race week and the reckoning between races happen away from the racetrack;
 * everything else happened with the engines running.
 */
export function whereOf(momentTag: string): FeudBeatWhere {
  return momentTag.startsWith('raceweek.') || momentTag.startsWith('season.') ? 'off' : 'track';
}

// ---------------------------------------------------------------------------
// The fold
// ---------------------------------------------------------------------------

export interface FeudHistoryInput {
  /** The season stream so far: every race already run, in order. */
  season?: readonly SeasonEvent[];
  /**
   * THIS week's ledger, which is not in the season stream yet — the week is
   * generated, shown, and only committed when the player takes the green.
   * Without this the card would show a feud with no visible cause, which is the
   * one thing the chevron exists to fix.
   */
  week?: readonly RuleEvent[];
  /** The race this week belongs to, when it is numbered. */
  weekRaceNumber?: number | null;
  weekRaceName?: string | null;
  /** raceNumber → the name it was run under. */
  raceNames?: ReadonlyMap<number, string>;
  maxBeats?: number;
}

/** Everyone an event actually MOVED. */
function moved(event: RuleEvent): Set<string> {
  return new Set(event.positionDeltas.map((delta) => delta.driverId));
}

/**
 * Every interaction between each pair, folded out of the ledger in one pass.
 *
 * Pairs that never met come back with an empty history rather than missing from
 * the map — a caller rendering a row should not have to tell "they have no
 * history" apart from "I forgot to ask for it".
 */
export function feudHistories(
  pairs: readonly FeudEntry[],
  input: FeudHistoryInput = {},
): Map<string, FeudHistory> {
  const max = input.maxBeats ?? DEFAULT_MAX_BEATS;
  const histories = new Map<string, FeudHistory>();
  const wanted = new Map<string, FeudEntry>();
  for (const pair of pairs) {
    if (pair.a === pair.b) continue;
    const key = feudKeyOf(pair.a, pair.b);
    if (wanted.has(key)) continue;
    wanted.set(key, pair);
    histories.set(key, {
      key,
      a: pair.a,
      b: pair.b,
      beats: [],
      total: 0,
      onTrack: 0,
      offTrack: 0,
      pointed: 0,
      races: [],
      firstRace: null,
      origin: null,
    });
  }
  if (wanted.size === 0) return histories;

  /**
   * The week in hand is not in `raceNames` — it has not been run. So its own
   * name is passed separately and used for its own race number, and for the
   * unnumbered case where a single exhibition race has no season around it.
   */
  const nameOfRace = (raceNumber: number | null): string | null => {
    if (raceNumber === null) return input.weekRaceName ?? null;
    const named = input.raceNames?.get(raceNumber);
    if (named !== undefined) return named;
    return raceNumber === input.weekRaceNumber ? (input.weekRaceName ?? null) : null;
  };

  const consider = (
    event: RuleEvent,
    sequence: number,
    raceNumber: number | null,
    turn: number | null,
  ): void => {
    const named = new Set(event.actorIds);
    const shoved = moved(event);
    if (named.size + shoved.size < 2) return;
    const aboutAFewMen = event.actorIds.length <= 3;
    for (const [key, pair] of wanted) {
      const bothNamed = named.has(pair.a) && named.has(pair.b);
      const bothMoved = shoved.has(pair.a) && shoved.has(pair.b);
      const pointed = bothNamed && aboutAFewMen;
      // ⚠ Named-but-not-moved by a field-wide event is a roll call, and being
      // moved by race week is being dealt a card. See the header.
      if (!pointed && !(bothMoved && whereOf(event.momentTag) === 'track')) continue;
      const history = histories.get(key);
      if (!history) continue;
      const where = whereOf(event.momentTag);
      const beat: FeudBeat = {
        id: `${sequence}:${event.momentTag}:${key}`,
        sequence,
        raceNumber,
        raceName: nameOfRace(raceNumber),
        turn,
        where,
        weight: pointed ? 'pointed' : 'shared',
        label: labelFor(event.momentTag),
        text: event.description,
        citation: event.ruleCitation,
        dice: event.dice,
        momentTag: event.momentTag,
      };
      history.beats.push(beat);
      history.total += 1;
      if (where === 'track') history.onTrack += 1;
      else history.offTrack += 1;
      if (pointed) {
        history.pointed += 1;
        if (history.origin === null) history.origin = beat;
      }
      if (raceNumber !== null && !history.races.includes(raceNumber)) history.races.push(raceNumber);
    }
  };

  for (const event of input.season ?? []) {
    consider(event, event.sequence, event.raceNumber, event.turn);
  }
  // The week in hand has no sequence numbers yet — it has not been committed to
  // the stream. `-1` marks that rather than pretending to a position.
  for (const event of input.week ?? []) {
    consider(event, -1, input.weekRaceNumber ?? null, null);
  }

  for (const history of histories.values()) {
    history.races.sort((x, y) => x - y);
    history.firstRace = history.races.length > 0 ? history.races[0] : null;
    if (history.beats.length > max) history.beats = history.beats.slice(history.beats.length - max);
  }
  return histories;
}

// ---------------------------------------------------------------------------
// Saying it in one line
// ---------------------------------------------------------------------------

const runIns = (n: number): string => `${n} run-in${n === 1 ? '' : 's'}`;

/**
 * The sentence on the closed chevron. It has to be worth reading BEFORE it is
 * opened, otherwise the control is just a shape — so it carries the count, the
 * split between the racetrack and everywhere else, and how far back it goes.
 *
 * `thisRace` is what stops it saying "going back to race 12" about a grudge
 * that started ninety seconds ago.
 */
export function historyLine(
  history: FeudHistory | null | undefined,
  thisRace: number | null = null,
): string | null {
  if (!history || history.total === 0) return null;
  const parts: string[] = [runIns(history.total)];
  if (history.onTrack > 0 && history.offTrack > 0) {
    parts.push(`${history.onTrack} on the racetrack, ${history.offTrack} off it`);
  } else if (history.onTrack > 0) {
    parts.push('all of it on the racetrack');
  } else {
    parts.push('all of it away from the racetrack');
  }
  if (history.firstRace !== null && thisRace !== null && history.firstRace >= thisRace) {
    parts.push('all of it this week');
  } else if (history.firstRace !== null) {
    parts.push(`going back to race ${history.firstRace}`);
  }
  return `${parts.join(' — ')}.`;
}
